<template>
	<div class="flex flex_column text_left alfa_portfolio_diversification">
		<!-- General Limits -->
		<p>
			{{ $t("investor.exchange.modal.alfa.portfolioDiversification.generalLimits.description") }}
		</p>
		<div class="light_bg_color default_radius margin_top margin_bottom margin_r">
				<span class="secondary_color">
					{{ $t("investor.exchange.modal.alfa.portfolioDiversification.generalLimits.select.description") }}
				</span>
			<md-field>
				<label>{{ $t("investor.exchange.modal.alfa.portfolioDiversification.generalLimits.select.placeholder") }}</label>
				<TbSelect v-model="generalLimits.selectedLimits" md-dense multiple
						  @md-selected="onGeneralLimitSelect"
						  :options="filterOnlyEnabledOptions(alfaConfig.generalLimits)"
						  options-value-field="key"
						  options-display-field="translation"
						  data-vv-name="general_limit_select"
						  v-validate="modelValidations.generalLimit"
				/>
			</md-field>
			<AlfaSettingsBlock v-for="generalLimitSetting in generalLimitSettings" :key="generalLimitSetting.key"
							   :condition-setting="generalLimitSetting"
							   :config="getConfigByKey(alfaConfig.generalLimits, generalLimitSetting.key)"
							   :allowed-values-getter="alfaValueOptions"
							   :currency="currency"
							   @remove="removeGeneralLimitSetting(generalLimitSetting)"
							   class="margin_top"
							   ref="condition"
			/>
		</div>
		<!-- Special Limit Groups -->
		<p>
			{{ $t("investor.exchange.modal.alfa.portfolioDiversification.specialLimits.description") }}
		</p>
		<div v-for="(groupSetting, groupIndex) in specialLimitGroupSettings" :key="groupIndex">
			<div class="special_limit_group_label_box default_radius w_fit_content font_bold margin_bottom">
				<span class="accent_color">
					{{ $t("investor.exchange.modal.alfa.portfolioDiversification.specialLimits.groupLabel", {index: groupIndex+1}) }}
				</span>
			</div>
			<div class="special_limit_group_box default_radius margin_bottom margin_r">
				<!-- Limits -->
				<span class="secondary_color">
					{{ $t("investor.exchange.modal.alfa.portfolioDiversification.specialLimits.limits.select.description") }}
				</span>
				<i class="fa fa-times close secondary_color pointer" @click="removeSpecialLimitGroup(groupIndex)"/>
				<md-field :class="[{ 'md-error': errors.has('limit_group_limit_select_' + groupIndex) }]">
					<label>{{ $t("investor.exchange.modal.alfa.portfolioDiversification.specialLimits.limits.select.placeholder") }}</label>
						<TbSelect v-model="specialLimitGroups.groups[groupIndex].selectedLimits" md-dense multiple
							  @md-selected="onSpecialLimitGroupLimitSelect(groupIndex)"
							  :options="filterOnlyEnabledOptions(alfaConfig.specialLimits)"
							  options-value-field="key"
							  options-display-field="translation"
							  :data-vv-name="'limit_group_limit_select_' + groupIndex"
							  v-validate="modelValidations.specialLimit"
							  :required="modelValidations.specialLimit.required"
					/>
					<span v-if="errors.has('limit_group_limit_select_' + groupIndex)" class="validation-error">
						{{ errors.first("limit_group_limit_select_" + groupIndex) }}
					</span>
				</md-field>
				<AlfaSettingsBlock v-for="specialLimitSetting in specialLimitGroupSettings[groupIndex].limits" :key="specialLimitSetting.key"
								   :condition-setting="specialLimitSetting"
								   :config="getConfigByKey(alfaConfig.specialLimits, specialLimitSetting.key)"
								   :allowed-values-getter="alfaValueOptions"
								   :currency="currency"
								   @remove="removeSpecialLimitSettingInGroup(groupSetting, groupIndex, specialLimitSetting)"
								   class="margin_top"
								   ref="condition"
				/>
				<hr class="special_limits_conditions_line margin_vertical">
				<!-- Conditions -->
				<span class="secondary_color">
					{{ $t("investor.exchange.modal.alfa.portfolioDiversification.specialLimits.conditions.select.description") }}
				</span>
				<md-field :class="[{ 'md-error': errors.has('limit_group_condition_select_' + groupIndex) }]">
					<label>{{ $t("investor.exchange.modal.alfa.portfolioDiversification.specialLimits.conditions.select.placeholder") }}</label>
					<TbSelect v-model="specialLimitGroups.groups[groupIndex].selectedConditions" md-dense multiple
							  @md-selected="onSpecialLimitGroupConditionSelect(groupIndex)"
							  :options="filterOnlyEnabledOptions(alfaConfig.specialLimitConditions)"
							  options-value-field="key"
							  options-display-field="translation"
							  :data-vv-name="'limit_group_condition_select_' + groupIndex"
							  v-validate="modelValidations.specialLimitCondition"
							  :required="modelValidations.specialLimitCondition.required"
					/>
					<span v-if="errors.has('limit_group_condition_select_' + groupIndex)" class="validation-error">
						{{ errors.first("limit_group_condition_select_" + groupIndex) }}
					</span>
				</md-field>
				<AlfaSettingsBlock v-for="specialConditionSetting in specialLimitGroupSettings[groupIndex].conditions" :key="specialConditionSetting.key"
								   :condition-setting="specialConditionSetting"
								   :config="getConfigByKey(alfaConfig.specialLimitConditions, specialConditionSetting.key)"
								   :allowed-values-getter="alfaValueOptions"
								   :currency="currency"
								   @remove="removeSpecialConditionSettingInGroup(groupSetting, groupIndex, specialConditionSetting)"
								   :list-selection-hint="$t('investor.exchange.modal.alfa.portfolioDiversification.specialLimits.conditions.selectionList.hint')"
								   class="margin_top"
								   ref="condition"
				/>
			</div>
		</div>
		<md-button class="md-primary w_fit_content" @click="addEmptySpecialLimitGroup">
			<span>{{ $t("investor.exchange.modal.alfa.portfolioDiversification.specialLimits.button.add") }}</span>
		</md-button>
	</div>
</template>

<script>
import AlfaSettingsBlock from "@/components/templates/investor/exchange/alfa/AlfaSettingsBlock";
import {mapGetters} from "vuex";
import {validateForm} from "@/validation/validation";

export default {
	components: {
		AlfaSettingsBlock,
	},
	props: {
		data: { type: Object, default: {} },
		currency: { type: String, default: "" }
	},
	data() {
		return {
			generalLimits: {
				selectedLimits: [],
				isInSyncProgress: false,
			},
			specialLimitGroups: {
				groups: [],
				isInSyncProgress: false,
			},
			emptySetting: {
				key: "",
				filterType: null,
				value: null,
				valueType: null
			},
			emptyLimitSettingsGroup: {
				limits: [],
				conditions: []
			},
			modelValidations: {
				generalLimit: {
					deprecated: {
						valueGetter: function(value) {
							return this.getConfigByKey(this.alfaConfig.generalLimits, value).isDeprecated;
						}.bind(this),
					}
				},
				specialLimit: {
					required: true,
					deprecated: {
						valueGetter: function(value) {
							return this.getConfigByKey(this.alfaConfig.specialLimits, value).isDeprecated;
						}.bind(this),
					}
				},
				specialLimitCondition: {
					required: true,
					deprecated: {
						valueGetter: function(value) {
							return this.getConfigByKey(this.alfaConfig.specialLimitConditions, value).isDeprecated;
						}.bind(this),
					}
				}
			}
		};
	},
	computed: {
		...mapGetters({
			alfaConfig: "alfa/config",
			alfaValueOptions: "alfa/valueOptions"
		}),
		generalLimitSettings() {
			return this.data.generalLimits;
		},
		specialLimitGroupSettings() {
			return this.data.specialLimitGroups;
		},
	},
	beforeMount() {
		this.syncSelectedGeneralLimitsWithSettings();
		this.syncSelectedSpecialLimitGroupsWithSettings();
	},
	methods: {
		getConfigByKey(config, key) {
			return config.find(config => config.key === key)
		},
		filterOnlyEnabledOptions(options) {
			return options.filter(o => !o.isDeprecated);
		},
		addEmptySpecialLimitGroup() {
			this.specialLimitGroupSettings.push(_.cloneDeep(this.emptyLimitSettingsGroup));
		},
		removeSpecialLimitGroup(groupIndex) {
			this.specialLimitGroupSettings.splice(groupIndex, 1);
		},
		removeGeneralLimitSetting(limitSetting) {
			let index = this.generalLimitSettings.indexOf(limitSetting);
			this.generalLimitSettings.splice(index, 1);
		},
		removeSpecialLimitSettingInGroup(groupSetting, groupIndex, limitSetting) {
			let index = groupSetting.limits.indexOf(limitSetting);
			groupSetting.limits.splice(index, 1);

			this.syncSelectedSpecialLimitGroupsWithSettings();
		},
		removeSpecialConditionSettingInGroup(groupSetting, groupIndex, conditionSetting) {
			let index = groupSetting.conditions.indexOf(conditionSetting);
			groupSetting.conditions.splice(index, 1);

			this.syncSelectedSpecialLimitGroupsWithSettings();
		},
		onGeneralLimitSelect() {
			if (!this.generalLimits.isInSyncProgress) {
				this.updateSettings(this.generalLimitSettings, this.generalLimits.selectedLimits);
			}
		},
		onSpecialLimitGroupLimitSelect(groupIndex) {
			if (!this.specialLimitGroups.isInSyncProgress) {
				this.updateSettings(this.specialLimitGroupSettings[groupIndex].limits, this.specialLimitGroups.groups[groupIndex].selectedLimits);
			}
		},
		onSpecialLimitGroupConditionSelect(groupIndex) {
			if (!this.specialLimitGroups.isInSyncProgress) {
				this.updateSettings(this.specialLimitGroupSettings[groupIndex].conditions, this.specialLimitGroups.groups[groupIndex].selectedConditions);
			}
		},
		updateSettings(settings, selectedKeys) {
			let previousSettings = [...settings];

			// clear current limit/condition settings, because we build the list again based on the current selection
			settings.splice(0);

			selectedKeys.forEach(selectedKey => {
				let previousSettingForSelected = previousSettings.find(previousSetting => previousSetting.key === selectedKey);
				if (previousSettingForSelected) {
					settings.push(previousSettingForSelected);
				} else {
					settings.push({
						...this.emptySetting,
						key: selectedKey
					});
				}
			});
		},
		syncSelectedGeneralLimitsWithSettings() {
			this.generalLimits.isInSyncProgress = true;

			this.generalLimits.selectedLimits = this.generalLimitSettings.map(limitSetting => limitSetting.key);

			this.$nextTick(() => this.generalLimits.isInSyncProgress = false);
		},
		syncSelectedSpecialLimitGroupsWithSettings() {
			this.specialLimitGroups.isInSyncProgress = true;

			// clear current special limit groups, because we build the list again based on the current selection
			this.specialLimitGroups.groups.splice(0);

			this.specialLimitGroupSettings.forEach(specialLimitGroupSetting => {
				this.specialLimitGroups.groups.push({
					selectedLimits: specialLimitGroupSetting.limits.map(limitSetting => limitSetting.key),
					selectedConditions: specialLimitGroupSetting.conditions.map(limitSetting => limitSetting.key),
				});
			});

			this.$nextTick(() => this.specialLimitGroups.isInSyncProgress = false);
		},
		validate() {
			return Promise.all([
				(this.$refs.condition ?? []).map((ref) => validateForm(ref,  ref.$validator)),
				validateForm(this, this.$validator)
			].flat());
		}
	},
	watch: {
		generalLimitSettings() {
			this.syncSelectedGeneralLimitsWithSettings();
		},
		specialLimitGroupSettings() {
			this.syncSelectedSpecialLimitGroupsWithSettings();
		}
	}
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/base_variables.scss";

.alfa_portfolio_diversification {
	.special_limit_group_label_box {
		background-color: #E9F2FD;
		padding: rem(10);
	}

	.special_limit_group_box {
		position: relative;
		background-color: #F3F3F3;
		padding: rem(10);

		> i.close {
			position: absolute;
			top: 5px;
			right: 5px;
		}
	}

	.special_limits_conditions_line {
		height: 1px;
		background-color: $inactive-color;
	}
}
</style>